import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Search, ChevronDown, ChevronUp, Save, X, Plus } from "lucide-react";
import "../styles/AdminUsersPage.css";
import "../styles/vocabManagementPage.css";
import { getSession } from "../session";
import apiFetch from "../apiFetch";
import { YEAR_OPTIONS, DISPLAY_YEAR_OPTIONS, tierLabel } from "../data/vocabYearGroups";

const PAGE_SIZE = 25;

// Only real (non-virtual) tiers — excludes virtual groups (10=11+Lower, 11=11+Higher)
const REAL_YEAR_OPTIONS = DISPLAY_YEAR_OPTIONS.filter(o => o.startTier === undefined);

function defaultAddTier(selectedTier) {
  if (selectedTier === 10) return 5; // 11+ Lower → Year 5
  if (selectedTier === 11) return 8; // 11+ Higher → Year 8
  return selectedTier;
}

const EMPTY_ADD_DRAFT = (tier) => ({
  word: "", definition: "", synonyms: "", antonyms: "",
  frequency: "1000", difficulty: "50", tier, active: true,
});

function joinArr(arr) {
  return Array.isArray(arr) ? arr.join(", ") : "";
}

function splitArr(str) {
  return str.split(",").map(s => s.trim()).filter(Boolean);
}

export default function VocabManagementPage() {
  const navigate = useNavigate();
  const session  = getSession();

  const [pageStatus,   setPageStatus]   = useState("loading");
  const [words,        setWords]        = useState([]);
  const [selectedTier, setSelectedTier] = useState(0);
  const [search,       setSearch]       = useState("");
  const [page,         setPage]         = useState(1);
  const [expandedId,   setExpandedId]   = useState(null);
  const [draft,        setDraft]        = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState("");
  const [toggleLoading, setToggleLoading] = useState(null);
  const [showAddForm,  setShowAddForm]   = useState(false);
  const [addDraft,     setAddDraft]      = useState(null);
  const [addSaving,    setAddSaving]     = useState(false);
  const [addError,     setAddError]      = useState("");

  // Admin guard
  useEffect(() => {
    apiFetch("/api/admin/check")
      .then(r => r.json())
      .then(d => { if (!d.isAdmin) navigate("/home"); })
      .catch(() => navigate("/home"));
  }, []); // eslint-disable-line

  const fetchWords = useCallback(async (tier) => {
    setPageStatus("loading");
    setExpandedId(null);
    setDraft(null);
    setPage(1);
    try {
      const res = await apiFetch(`/api/vocab-admin/words?tier=${tier}`);
      if (res.status === 403) { navigate("/home"); return; }
      if (!res.ok) { setPageStatus("error"); return; }
      const data = await res.json();
      setWords(data.words || []);
      setPageStatus("ok");
    } catch {
      setPageStatus("error");
    }
  }, [navigate]);

  useEffect(() => { fetchWords(selectedTier); }, [selectedTier, fetchWords]);

  // ── Filtered + paginated list ──
  const filtered = words.filter(w =>
    w.word.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageWords  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  // ── Expand / collapse row ──
  const toggleExpand = (word) => {
    if (expandedId === word.id) {
      setExpandedId(null);
      setDraft(null);
      setSaveError("");
    } else {
      setExpandedId(word.id);
      setDraft({
        definition: word.definition || "",
        synonyms:   joinArr(word.synonyms),
        antonyms:   joinArr(word.antonyms),
        frequency:  word.frequency ?? "",
        difficulty: word.difficulty ?? "",
        active:     word.active !== false,
      });
      setSaveError("");
    }
  };

  // ── Quick active toggle ──
  const handleToggleActive = async (word) => {
    setToggleLoading(word.id);
    try {
      const res = await apiFetch(`/api/vocab-admin/word/${word.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: !word.active }),
      });
      if (!res.ok) throw new Error();
      setWords(prev => prev.map(w => w.id === word.id ? { ...w, active: !w.active } : w));
      if (draft && expandedId === word.id) {
        setDraft(d => ({ ...d, active: !word.active }));
      }
    } catch {
      // silently ignore
    } finally {
      setToggleLoading(null);
    }
  };

  // ── Save full edit ──
  const handleSave = async () => {
    if (!draft || expandedId === null) return;
    setSaving(true);
    setSaveError("");
    try {
      const res = await apiFetch(`/api/vocab-admin/word/${expandedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          definition: draft.definition,
          synonyms:   splitArr(draft.synonyms),
          antonyms:   splitArr(draft.antonyms),
          frequency:  draft.frequency !== "" ? Number(draft.frequency) : undefined,
          difficulty: draft.difficulty !== "" ? Number(draft.difficulty) : undefined,
          active:     draft.active,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setSaveError(data.error || "Failed to save"); return; }
      setWords(prev => prev.map(w => w.id === expandedId ? { ...data.word, active: data.word.active !== false } : w));
      setExpandedId(null);
      setDraft(null);
    } catch {
      setSaveError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Open add form ──
  const openAddForm = () => {
    setExpandedId(null);
    setDraft(null);
    setAddDraft(EMPTY_ADD_DRAFT(defaultAddTier(selectedTier)));
    setAddError("");
    setShowAddForm(true);
  };

  const closeAddForm = () => { setShowAddForm(false); setAddDraft(null); setAddError(""); };

  // ── Save new word ──
  const handleAddSave = async () => {
    if (!addDraft) return;
    if (!addDraft.word.trim())       { setAddError("Word is required."); return; }
    if (!addDraft.definition.trim()) { setAddError("Definition is required."); return; }

    setAddSaving(true);
    setAddError("");
    try {
      const res = await apiFetch("/api/vocab-admin/word", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          word:       addDraft.word.trim(),
          definition: addDraft.definition.trim(),
          synonyms:   splitArr(addDraft.synonyms),
          antonyms:   splitArr(addDraft.antonyms),
          frequency:  addDraft.frequency !== "" ? Number(addDraft.frequency) : undefined,
          difficulty: addDraft.difficulty !== "" ? Number(addDraft.difficulty) : undefined,
          tier:       addDraft.tier,
          active:     addDraft.active,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setAddError(data.error || "Failed to add word."); return; }
      closeAddForm();
      fetchWords(selectedTier); // re-fetch to show new word in list
    } catch {
      setAddError("Network error. Please try again.");
    } finally {
      setAddSaving(false);
    }
  };

  // ── Render ──
  if (pageStatus === "loading") {
    return (
      <div className="vm-page">
        <div className="vm-center"><p className="adm-muted">Loading…</p></div>
      </div>
    );
  }

  if (pageStatus === "error") {
    return (
      <div className="vm-page">
        <div className="vm-center">
          <p className="adm-muted">Failed to load vocabulary.</p>
          <button className="adm-btn adm-btn-secondary" onClick={() => fetchWords(selectedTier)}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="vm-page">

      {/* ── Header ── */}
      <div className="vm-header">
        <div className="vm-header-left">
          <GraduationCap size={22} className="vm-header-icon" />
          <div>
            <h1 className="vm-title">Vocabulary Management</h1>
            <p className="vm-subtitle">Edit words, definitions, synonyms &amp; antonyms</p>
          </div>
        </div>
        <div className="vm-header-right">
          <div className="vm-year-pill">
            <GraduationCap size={14} className="vm-year-icon" />
            <span className="vm-year-lbl">Year group</span>
            <select
              className="vm-year-select"
              value={selectedTier}
              onChange={e => setSelectedTier(Number(e.target.value))}
            >
              {DISPLAY_YEAR_OPTIONS.map(opt => (
                <option key={opt.tier} value={opt.tier}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Search + count ── */}
      <div className="vm-toolbar">
        <div className="vm-search-wrap">
          <Search size={15} className="vm-search-icon" />
          <input
            className="vm-search"
            placeholder="Search words…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
          {search && (
            <button className="vm-search-clear" onClick={() => handleSearch("")} aria-label="Clear search">
              <X size={13} />
            </button>
          )}
        </div>
        <span className="vm-count">
          {filtered.length} word{filtered.length !== 1 ? "s" : ""} in {tierLabel(selectedTier)}
          {search && ` matching "${search}"`}
        </span>
        <button className="vm-add-btn" onClick={openAddForm} disabled={showAddForm}>
          <Plus size={14} /> Add Word
        </button>
      </div>

      {/* ── Add Word form ── */}
      {showAddForm && addDraft && (
        <div className="vm-add-panel">
          <div className="vm-add-panel-header">
            <strong className="vm-add-title">Add New Word</strong>
            <button className="vm-edit-btn" onClick={closeAddForm} aria-label="Close">
              <X size={15} />
            </button>
          </div>
          <div className="vm-expand-form">
            <div className="vm-form-row vm-form-row--2col">
              <label className="vm-label">
                Word <span className="vm-required">*</span>
                <input
                  className="vm-input"
                  value={addDraft.word}
                  onChange={e => setAddDraft(d => ({ ...d, word: e.target.value }))}
                  placeholder="e.g. perseverance"
                  autoFocus
                />
              </label>
              <label className="vm-label">
                Year group <span className="vm-required">*</span>
                <select
                  className="vm-input"
                  value={addDraft.tier}
                  onChange={e => setAddDraft(d => ({ ...d, tier: Number(e.target.value) }))}
                >
                  {REAL_YEAR_OPTIONS.map(opt => (
                    <option key={opt.tier} value={opt.tier}>{opt.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="vm-form-row">
              <label className="vm-label">
                Definition <span className="vm-required">*</span>
                <textarea
                  className="vm-textarea"
                  rows={4}
                  value={addDraft.definition}
                  onChange={e => setAddDraft(d => ({ ...d, definition: e.target.value }))}
                  placeholder="Enter the definition…"
                />
              </label>
            </div>
            <div className="vm-form-row vm-form-row--2col">
              <label className="vm-label">
                Synonyms <span className="vm-hint">(comma-separated)</span>
                <input
                  className="vm-input"
                  value={addDraft.synonyms}
                  onChange={e => setAddDraft(d => ({ ...d, synonyms: e.target.value }))}
                  placeholder="e.g. happy, joyful, content"
                />
              </label>
              <label className="vm-label">
                Antonyms <span className="vm-hint">(comma-separated)</span>
                <input
                  className="vm-input"
                  value={addDraft.antonyms}
                  onChange={e => setAddDraft(d => ({ ...d, antonyms: e.target.value }))}
                  placeholder="e.g. sad, miserable"
                />
              </label>
            </div>
            <div className="vm-form-row vm-form-row--3col">
              <label className="vm-label">
                Frequency
                <input
                  className="vm-input"
                  type="number"
                  min={0}
                  value={addDraft.frequency}
                  onChange={e => setAddDraft(d => ({ ...d, frequency: e.target.value }))}
                />
              </label>
              <label className="vm-label">
                Difficulty
                <input
                  className="vm-input"
                  type="number"
                  min={0}
                  value={addDraft.difficulty}
                  onChange={e => setAddDraft(d => ({ ...d, difficulty: e.target.value }))}
                />
              </label>
              <label className="vm-label vm-label--toggle">
                Active
                <button
                  className={`vm-toggle ${addDraft.active ? "vm-toggle--on" : "vm-toggle--off"}`}
                  onClick={() => setAddDraft(d => ({ ...d, active: !d.active }))}
                  type="button"
                >
                  {addDraft.active ? "Active" : "Inactive"}
                </button>
              </label>
            </div>

            {addError && <p className="vm-save-error">{addError}</p>}

            <div className="vm-form-actions">
              <button
                className="adm-btn adm-btn-primary vm-save-btn"
                onClick={handleAddSave}
                disabled={addSaving}
              >
                <Plus size={14} />
                {addSaving ? "Adding…" : "Add Word"}
              </button>
              <button
                className="adm-btn adm-btn-secondary"
                onClick={closeAddForm}
                disabled={addSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="vm-body">
        <div className="adm-table-wrap">
          <table className="adm-table vm-table">
            <thead>
              <tr>
                <th className="vm-col-word">Word</th>
                <th className="vm-col-active">Active</th>
                <th className="vm-col-def">Definition</th>
                <th className="vm-col-syn">Synonyms</th>
                <th className="vm-col-ant">Antonyms</th>
                <th className="vm-col-num">Freq</th>
                <th className="vm-col-num">Diff</th>
                <th className="vm-col-edit"></th>
              </tr>
            </thead>
            <tbody>
              {pageWords.length === 0 && (
                <tr><td colSpan={8} className="adm-empty">No words found.</td></tr>
              )}
              {pageWords.map(word => {
                const isExpanded = expandedId === word.id;
                const isInactive = !word.active;
                return (
                  <Fragment key={word.id}>
                    <tr
                      className={`vm-row ${isInactive ? "vm-row--inactive" : ""} ${isExpanded ? "vm-row--expanded" : ""}`}
                    >
                      <td className="vm-col-word">
                        <strong>{word.word}</strong>
                      </td>
                      <td className="vm-col-active">
                        <button
                          className={`vm-toggle ${word.active ? "vm-toggle--on" : "vm-toggle--off"}`}
                          onClick={() => handleToggleActive(word)}
                          disabled={toggleLoading === word.id}
                          title={word.active ? "Click to deactivate" : "Click to activate"}
                        >
                          {word.active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="vm-col-def vm-truncate">{word.definition || "—"}</td>
                      <td className="vm-col-syn vm-truncate">{joinArr(word.synonyms) || "—"}</td>
                      <td className="vm-col-ant vm-truncate">{joinArr(word.antonyms) || "—"}</td>
                      <td className="vm-col-num adm-muted-cell">{word.frequency ?? "—"}</td>
                      <td className="vm-col-num adm-muted-cell">{word.difficulty ?? "—"}</td>
                      <td className="vm-col-edit">
                        <button
                          className="vm-edit-btn"
                          onClick={() => toggleExpand(word)}
                          aria-label={isExpanded ? "Close edit" : "Edit word"}
                        >
                          {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </button>
                      </td>
                    </tr>

                    {isExpanded && draft && (
                      <tr key={`${word.id}-edit`} className="vm-expand-row">
                        <td colSpan={8} className="vm-expand-cell">
                          <div className="vm-expand-form">
                            <div className="vm-form-row">
                              <label className="vm-label">
                                Definition
                                <textarea
                                  className="vm-textarea"
                                  rows={4}
                                  value={draft.definition}
                                  onChange={e => setDraft(d => ({ ...d, definition: e.target.value }))}
                                />
                              </label>
                            </div>
                            <div className="vm-form-row vm-form-row--2col">
                              <label className="vm-label">
                                Synonyms <span className="vm-hint">(comma-separated)</span>
                                <input
                                  className="vm-input"
                                  value={draft.synonyms}
                                  onChange={e => setDraft(d => ({ ...d, synonyms: e.target.value }))}
                                  placeholder="e.g. happy, joyful, content"
                                />
                              </label>
                              <label className="vm-label">
                                Antonyms <span className="vm-hint">(comma-separated)</span>
                                <input
                                  className="vm-input"
                                  value={draft.antonyms}
                                  onChange={e => setDraft(d => ({ ...d, antonyms: e.target.value }))}
                                  placeholder="e.g. sad, miserable"
                                />
                              </label>
                            </div>
                            <div className="vm-form-row vm-form-row--3col">
                              <label className="vm-label">
                                Frequency
                                <input
                                  className="vm-input"
                                  type="number"
                                  min={0}
                                  value={draft.frequency}
                                  onChange={e => setDraft(d => ({ ...d, frequency: e.target.value }))}
                                />
                              </label>
                              <label className="vm-label">
                                Difficulty
                                <input
                                  className="vm-input"
                                  type="number"
                                  min={0}
                                  value={draft.difficulty}
                                  onChange={e => setDraft(d => ({ ...d, difficulty: e.target.value }))}
                                />
                              </label>
                              <label className="vm-label vm-label--toggle">
                                Active
                                <button
                                  className={`vm-toggle ${draft.active ? "vm-toggle--on" : "vm-toggle--off"}`}
                                  onClick={() => setDraft(d => ({ ...d, active: !d.active }))}
                                  type="button"
                                >
                                  {draft.active ? "Active" : "Inactive"}
                                </button>
                              </label>
                            </div>

                            {saveError && <p className="vm-save-error">{saveError}</p>}

                            <div className="vm-form-actions">
                              <button
                                className="adm-btn adm-btn-primary vm-save-btn"
                                onClick={handleSave}
                                disabled={saving}
                              >
                                <Save size={14} />
                                {saving ? "Saving…" : "Save"}
                              </button>
                              <button
                                className="adm-btn adm-btn-secondary"
                                onClick={() => { setExpandedId(null); setDraft(null); setSaveError(""); }}
                                disabled={saving}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="vm-pagination">
            <button
              className="vm-page-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              ← Prev
            </button>
            <span className="vm-page-info">Page {safePage} of {totalPages}</span>
            <button
              className="vm-page-btn"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
